import { api } from './client';
/*  Lo stesso contratto del calcolo sulla commessa: Google propone, il tecnico decide.
    Importato da lì invece di riscritto — è la stessa risposta dello stesso servizio.  */
import type { CalcoloTrasferta } from './commesse';
/*  L'offerta a confronto e' la stessa che mostra la scheda dell'articolo, e si importa da
    li' invece di riscriverla: due dichiarazioni dello stesso contratto divergono, e il
    controllo dei contratti ne verificherebbe una sola — quella che trova per prima.  */
import type { OffertaConfrontata } from './articoli';

/*  Specchio dei contratti di TeraWorks.Application.Preventivi.

    Ogni scrittura risponde con il preventivo intero, e non con la cosa che ha toccato:
    cambiare una quantità cambia le ore, che cambiano i giorni, che cambiano i viaggi, che
    cambiano le trasferte, che cambiano i costi generali, che cambiano il prezzo. Rifare
    quella catena qui vorrebbe dire una seconda copia delle formule del dominio — e due
    copie di una formula prima o poi dicono due numeri diversi, su un documento che
    stabilisce un prezzo.  */

export type StatoPreventivo =
  | 'Bozza'
  | 'InRedazione'
  | 'Inviato'
  | 'Confermato'
  | 'NonAggiudicato'
  | 'Annullato';

export interface RigaPreventivoLetta {
  id: string;
  rigaPadreId: string | null;
  /** 1 capitolo, 2 sottocapitolo, 3 articolo: l'indentazione la fa chi disegna. */
  livello: number;
  posizione: number;
  descrizione: string;
  siglaProduttore: string | null;
  codiceArticoloProduttore: string | null;
  unitaMisura: string | null;
  quantita: number;
  /*  Il listino lordo prima dello sconto, fotografato quando l'articolo è entrato. Non
      serve a nessun conto: serve a controllare che il netto accanto sia giusto. Null su
      una voce a mano, che un listino non ce l'ha.  */
  prezzoListinoUnitario: number | null;
  costoUnitarioMateriale: number;
  oreUnitarie: number;
  /** L'importo della posa a corpo, o null quando la manodopera è a ore. */
  posaACorpo: number | null;
  /*  Questa voce la fa un altro. Il costo non si sposta — resta quantità per costo
      unitario — ma finisce nella voce «subappalti» invece che in «materiali».  */
  subappalto: boolean;
  codiceFornitoreMago: string | null;
  fornitore: string | null;
  fonteCosto: string;
  prezzoBloccato: boolean;
  visibileInStampa: boolean;
  /*  Come si presenta al cliente la distinta di questo capitolo: 'Aperta',
      'PrezziSullaTesta' oppure 'Chiusa'. Su un articolo è sempre 'Aperta'.  */
  distinta: string;
  opzionale: boolean;
  /*  Se questa riga è andata in commessa. Null finché nessuno ha deciso — l'offerta non
      è confermata, o lo è stata prima che la domanda esistesse — e falsa quando alla
      conferma è stata guardata e lasciata fuori. Sono due cose diverse.  */
  inCommessa: boolean | null;
  /** Riga vuota: uno stacco fra due blocchi, senza descrizione e senza conti. */
  separatore: boolean;
  ricaricoSpecificoPercentuale: number | null;
  /*  Lo sconto al cliente scritto SU QUESTA riga. Null vuol dire che non ne ha uno
      suo, non che non ne subisce: quello del capitolo sta in `scontoApplicato`.  */
  scontoClientePercentuale: number | null;
  giaComprensivoDiMargine: boolean;
  costoMateriale: number;
  /** Lo sconto che si è spuntato: calcolato dal listino e dal netto, non salvato. */
  scontoPercentuale: number | null;
  ore: number;
  /*  Su un articolo il costo e il prezzo suoi; su un capitolo la somma di quello che gli
      sta sotto, e `aggregato` dice quale dei due si sta guardando.  */
  costo: number;
  prezzo: number;
  aggregato: boolean;
  /** Il ricarico che questa riga applica davvero. Null quando non se ne applica nessuno. */
  ricaricoApplicato: number | null;
  /** Da dove viene: Generale, Capitolo, Riga, GiaNelCosto. La scheda lo dice con un colore. */
  fonteRicarico: string;
  /** Lo sconto che questa riga subisce davvero, quello ereditato dal capitolo compreso. */
  scontoApplicato: number | null;
  /** Da dove viene: Riga, Capitolo oppure Nessuno. La scheda lo dice con un colore. */
  fonteSconto: string;
}

export interface SpesaAccessoriaLetta {
  id: string;
  tipo: string;
  nome: string;
  descrizione: string | null;
  importo: number | null;
  percentuale: number | null;
  /** Quanto vale davvero su questo preventivo: su una percentuale è il conto già fatto. */
  importoCalcolato: number;
  /*  Falso vuol dire «di cui»: l'importo è già dentro il prezzo e si scrive a parte solo
      perché va dichiarato. È il caso normale degli oneri della sicurezza.  */
  nelTotale: boolean;
  /** Se il cliente la legge come voce sua nel riepilogo, o se sta dentro il prezzo senza dirsi. */
  visibileInStampa: boolean;
  /*  Quanto di questa spesa è andato in commessa. Nullo finché nessuno ha confermato:
      «non ancora deciso» e «deciso di non portarne niente» sono due cose diverse.  */
  quotaInCommessa: number | null;
}

export interface TrasfertaLetta {
  giorni: number;
  personeInSquadra: number;
  mezziPerGiornata: number;
  giornateUomo: number;
  viaggi: number;
  viaggiCorretti: boolean;
  /** Quanti ne direbbe il calcolo: resta leggibile anche quando sono corretti a mano. */
  viaggiCalcolati: number;
  kmAndataRitorno: number | null;
  /** La distanza dalla sede, sola andata: il numero con cui si leggono le soglie dei pasti. */
  distanzaSolaAndata: number;
  tariffaKm: number;
  indennitaGiornaliera: number;
  costoKm: number;
  costoIndennita: number;
  pranzi: number;
  cene: number;
  costoPranzo: number;
  costoCena: number;
  costoPasti: number;
  /** Le notti fuori, per persona: le stesse delle cene, e da qui escono i letti. */
  notti: number;
  costoPernottamento: number;
  /** Notti per costo: resta leggibile anche quando il totale è forzato. */
  costoAlbergoCalcolato: number;
  albergoForzato: boolean;
  costoAlbergo: number;
  pernotta: boolean;
  /** Vero quando il pernotto l'ha deciso il tecnico invece di lasciarlo dedurre. */
  pernottoDeciso: boolean;
  pernottoCalcolato: boolean;
  /** Quanto direbbe il calcolo: resta a schermo anche quando l'importo è forzato. */
  importoCalcolato: number;
  importoForzato: boolean;
  importo: number;
}

export interface VoceAccessoriaLetta {
  nome: string;
  importo: number;
  calcolata: boolean;
  /** Falso vuol dire «di cui»: già dentro il prezzo, si scrive a parte perché va dichiarato. */
  nelTotale: boolean;
  /** Se il cliente la legge come voce sua nel riepilogo, o se sta dentro il prezzo senza dirsi. */
  visibileInStampa: boolean;
}

export interface ContiPreventivo {
  oreTotali: number;
  costoMateriali: number;
  costoManodopera: number;
  /** Quanto di questo lavoro lo fa qualcun altro. È già dentro il costo diretto. */
  costoSubappalti: number;
  costoDiretto: number;
  trasferta: TrasfertaLetta;
  /** Le voci come si leggono: senza le trasferte, quando stanno dentro l'ora. */
  accessorie: VoceAccessoriaLetta[];
  /** Quanto la trasferta aggiunge a ogni ora. Zero quando si legge come voce sua. */
  supplementoOrario: number;
  /*  Quanto pesa la trasferta su ogni ora di manodopera, SEMPRE — anche quando la
      trasferta sta in una voce sua, cioè quando `supplementoOrario` è zero. È la nota
      interna chiesta in riunione, e non va in offerta.  */
  trasfertaPerOra: number;
  speseAccessorie: number;
  /** Quanto del prezzo è dichiarato come «di cui». Fuori da ogni somma. */
  diCuiEsposto: number;
  costiGenerali: number;
  costoTotale: number;
  /** Prima dello sconto o dell'aumento globale: è la somma delle righe. */
  prezzoPieno: number;
  /** Lo sconto (negativo) o l'aumento (positivo) in euro. Zero quando non c'è. */
  aggiustamento: number;
  prezzo: number;
  margine: number;
  /** Sul prezzo e non sul costo: «quanto di quello che incasso mi resta». */
  marginePercentuale: number | null;
  costoOpzionali: number;
  prezzoOpzionali: number;
}


/*  LE TRE STRADE DELLA TRASFERTA IN OFFERTA.

    Nascosta: non si nomina, e la sua quota si spalma muta sui capitoli dentro la riga
    della manodopera. VoceUnica: una riga del riepilogo, in fondo, accanto agli altri
    capitoli. NeiCapitoli: una riga «Trasferte» dentro ciascun capitolo, ripartita in
    proporzione alle ore.

    Il Totale è lo stesso in tutt'e tre: cambia solo quanto se ne racconta.  */
export type TrasfertaInOfferta = 'Nascosta' | 'VoceUnica' | 'NeiCapitoli';

export interface DettaglioPreventivo {
  id: string;
  /** Il numero base, senza revisione: è quello su cui si cerca. */
  numero: string | null;
  /** Come si legge e come si stampa: «26-0006/1» dalla prima revisione in poi. */
  numeroCompleto: string | null;
  codiceSede: string;
  revisione: number;
  /** Il documento da cui questa revisione è stata copiata. Null sulla prima stesura. */
  revisioneDiId: string | null;
  stato: StatoPreventivo;
  codiceClienteMago: string;
  cliente: string | null;
  oggettoPerCliente: string | null;
  descrizioneInterna: string;
  codiceCategoria: string;
  dataPreventivo: string | null;
  dataValidita: string | null;
  dataPrevistaInizioLavori: string | null;
  dataPrevistaFineLavori: string | null;
  tecnicoResponsabileId: string | null;
  tecnicoResponsabile: string | null;
  /*  Chi ha aperto il documento e come lo si raggiunge: sull'offerta stanno in testata,
      ed è a lui che il cliente risponde. Il tecnico responsabile può essere un altro.  */
  redattore: string | null;
  emailRedattore: string | null;
  /*  Chi ha riletto l'offerta e quando. Nulli finché nessuno l'ha fatto, e tornano nulli
      a ogni modifica: la firma vale su quello che è stato firmato e su nient'altro.  */
  controllatoDa: string | null;
  controllatoIl: string | null;
  tariffaManodoperaOraria: number;
  costiGeneraliPercentuale: number;
  ricaricoGeneralePercentuale: number;
  /** Se il ricarico tocca anche le ore. Di norma no: la tariffa è già netta. */
  ricaricaManodopera: boolean;
  /** Sconto (negativo) o aumento (positivo) su tutto il preventivo, in percentuale. */
  aggiustamentoPercentuale: number;
  indirizzoCantiere: string | null;
  kmTrasferta: number | null;
  personeInSquadra: number;
  orePerGiornata: number;
  mezziPerGiornata: number;
  viaggiPrevisti: number | null;
  /** La trasferta si legge dentro il costo dell'ora invece che come voce sua. */
  trasfertaNelCostoOrario: boolean;
  /*  Dove la trasferta si legge in offerta: 'Nascosta', 'VoceUnica' — una riga del
      riepilogo — oppure 'NeiCapitoli', una riga dentro ciascun capitolo in proporzione
      alle ore. È una stringa e non un numero perché il nome si legge nel traffico e in
      una console: un 2 che gira per la rete non dice niente a nessuno.  */
  trasfertaInOfferta: TrasfertaInOfferta;
  /*  Se i costi generali si dichiarano in offerta come voce del riepilogo. Spenti, la
      loro quota si spalma sui capitoli: il Totale è lo stesso, cambia solo quanto se ne
      racconta.  */
  costiGeneraliVisibiliInStampa: boolean;
  /** L'importo deciso a mano, zero compreso. Null è «lascia calcolare». */
  importoTrasfertaForzato: number | null;
  /*  I costi dei pasti e della notte come sono scritti su QUESTO documento. Stanno qui
      e non solo dentro `conti.trasferta`, perché là sono un risultato mentre questi
      sono il dato da cui esce — la scheda ci mette dentro delle caselle, e una casella
      che legge da un conto è una casella che non si può cambiare.

      (Il controllo dei contratti legge riga per riga: una riga di commento che comincia
      con «parola:» la scambia per un campo. Ci è già cascato.)  */
  costoPranzo: number;
  costoCena: number;
  costoPernottamento: number;
  /** Il totale dell'albergo deciso a mano. Null lascia il conto a notte. */
  importoAlbergoForzato: number | null;
  inviatoIl: string | null;
  numeroOrdineCliente: string | null;
  /*  La commessa nata dalla conferma. Sta nel dettaglio perché «su quale numero si
      segnano le ore» è una domanda che si fa guardando l'offerta.  */
  commessaId: string | null;
  numeroCommessa: string | null;
  /*  Quanto vale la parte che il cliente ha preso, quando non ha preso tutto. Null nel
      caso normale — offerta intera, o non ancora confermata — e allora il numero da dire
      resta uno solo.  */
  prezzoInCommessa: number | null;
  /** Vero finché si può ancora cambiare qualcosa: dopo l'invio il documento è uscito. */
  modificabile: boolean;
  /*  I testi che finiscono in offerta. Stanno nel dettaglio e non in una chiamata a
      parte perché si spuntano nella stessa schermata in cui si scrivono le righe.  */
  notaPerCliente: string | null;
  /*  A chi è indirizzata QUESTA offerta. Sta sul documento e non in anagrafica: un
      cliente grosso ha un referente per reparto, e cambiarlo di là per intestare
      un'offerta vorrebbe dire cambiarlo per tutte.  */
  allaCorteseAttenzione: string | null;
  /*  Di che lavoro parla l'offerta: «Elettrico», «Meccanico», … Decide quali clausole
      predefinite si trovano gia' spuntate quando la si apre, e quali si vedono
      filtrando. Null vuol dire «non deciso».  */
  categoriaLavoro: string | null;
  letteraId: string | null;
  /** Il codice Mago della modalità di pagamento scelta — «B08». Null: non scelta. */
  codicePagamentoMago: string | null;
  /*  La frase da mostrare: quella congelata all'invio se l'offerta è uscita, quella
      che l'anagrafica dice oggi altrimenti. La scheda la usa per far leggere cosa è
      stato scelto senza andare a cercare il codice nella tendina.  */
  pagamento: string | null;
  /** Le condizioni, gli oneri e le esclusioni spuntati per questa offerta. */
  testiScelti: string[];
  vociLibere: VoceLiberaLetta[];
  conti: ContiPreventivo;
  righe: RigaPreventivoLetta[];
  spese: SpesaAccessoriaLetta[];
}

export interface TestataPreventivo {
  descrizioneInterna: string;
  oggettoPerCliente: string | null;
  dataPreventivo: string | null;
  dataValidita: string | null;
  dataPrevistaInizioLavori: string | null;
  dataPrevistaFineLavori: string | null;
  tariffaManodoperaOraria: number;
  costiGeneraliPercentuale: number;
  ricaricoGeneralePercentuale: number;
  tecnicoResponsabileId: string | null;
  ricaricaManodopera?: boolean;
  /** Il «ricalcolo globale»: -10 sconta di un decimo, +5 aumenta del 5%. */
  aggiustamentoPercentuale?: number;
}

/*  Le persone dividono i giorni — a spostarsi è il furgone, non la testa — e i mezzi li
    moltiplicano. `viaggi` null li lascia calcolare.  */
export interface CantierePreventivo {
  indirizzoCantiere: string | null;
  kmTrasferta: number | null;
  personeInSquadra: number;
  orePerGiornata: number;
  viaggi: number | null;
  mezziPerGiornata?: number;
  /** La trasferta dentro l'ora invece che come voce sua. */
  trasfertaNelCostoOrario?: boolean;
  /** Deciso a mano, zero compreso. Null lascia calcolare. */
  importoTrasfertaForzato?: number | null;
  /** Se si dorme fuori. Null lascia decidere a chilometri e giorni, ed è il caso normale. */
  pernotta?: boolean | null;
  /*  I costi dei pasti e della notte su questo documento. Qui null vuol dire «lascia
      quello che c'è» e non «azzera»: è la direzione sicura. `importoAlbergoForzato`
      invece segue la regola dei «forzato» qui sopra — null è «lascia calcolare».  */
  costoPranzo?: number | null;
  costoCena?: number | null;
  costoPernottamento?: number | null;
  importoAlbergoForzato?: number | null;
  /*  Il costo al chilometro su QUESTA offerta. Il valore per tutti sta nei parametri, da
      dove arriva quando il preventivo nasce; qui si riscrive per un cantiere raggiunto
      col mezzo grande o per un rimborso concordato diverso. Null lascia quello che c'è. */
  tariffaKm?: number | null;
}

/*  Cosa è cambiato quando si sono sbloccati i prezzi. Chi sblocca ha appena lasciato che
    una procedura riscrivesse dei prezzi concordati: deve poter guardare riga per riga
    cosa si è mosso prima di mandare l'offerta.  */
export interface PrezzoAggiornato {
  rigaId: string;
  descrizione: string;
  prima: number;
  dopo: number;
}

export interface EsitoSbloccoPrezzi {
  preventivo: DettaglioPreventivo;
  aggiornati: PrezzoAggiornato[];
  righeSbloccate: number;
  /** Righe sbloccate ma lasciate al loro prezzo: non hanno un codice da cercare. */
  senzaListino: number;
}

/*  Chi altro offre l'articolo di questa riga, col costo di adesso accanto.

    `motivo` è la risposta quando non c'è niente da confrontare, ed è il caso normale e non
    l'eccezione: un articolo lo offre spesso un fornitore solo, o nessuno.  */
export interface ConfrontoRiga {
  rigaId: string;
  siglaProduttore: string | null;
  codiceArticoloProduttore: string | null;
  descrizioneArticolo: string | null;
  costoAttuale: number;
  fonteAttuale: string;
  prezzoBloccato: boolean;
  prezzoListinoProduttore: number | null;
  offerte: OffertaConfrontata[];
  /** Lette e non adottabili: accanto a ognuna c'è scritto perché. */
  fuoriConfronto: OffertaConfrontata[];
  /** L'offerta che corrisponde al costo di adesso, se è una sola. */
  offertaInUso: string | null;
  motivo: string | null;
}

export interface PrezzoDaOfferta {
  offertaId: string;
}

/** `dopoRigaId` la mette sotto quella riga, `primaRigaId` sopra; nessuno dei due, in fondo. */
export interface NuovaRigaPreventivo {
  livello: number;
  descrizione: string;
  sottoRigaId?: string | null;
  dopoRigaId?: string | null;
  primaRigaId?: string | null;
  /*  L'articolo del catalogo che questa riga rappresenta. Con lui la riga nasce già col
      codice del produttore, l'unità di misura e il costo della migliore offerta, con la
      sua provenienza. Vuoto per le voci a mano, che non sono materiale che qualcuno
      vende.  */
  articoloId?: string | null;
  /*  Uno stacco fra due blocchi invece di una voce: livello e descrizione vengono
      ignorati, una riga vuota e' sempre di livello articolo e non ha niente scritto.  */
  separatore?: boolean;
}

/*  Le quattro direzioni. «Su» e «Giu» muovono fra le righe dello stesso capitolo;
    «Dentro» porta la riga nel capitolo che la precede, «Fuori» la tira accanto al proprio.
    Quello che si sposta è sempre la riga con tutto quello che le sta sotto.  */
export type VersoSpostamento = 'Su' | 'Giu' | 'Dentro' | 'Fuori';

export interface ModificaRigaPreventivo {
  descrizione: string;
  quantita: number;
  oreUnitarie: number;
  /*  La posa a corpo: un importo invece di ore per tariffa. Null è «a ore», che è il
      caso normale; zero è «la posa non si paga», che è una decisione. Le ore restano e
      dicono quanto dura il cantiere — da lì escono giorni, viaggi e pasti.  */
  posaACorpo?: number | null;
  /*  Questa voce la fa un altro, e chi. Spegnendola il fornitore si cancella. */
  subappalto?: boolean;
  codiceFornitoreMago?: string | null;
  fornitore?: string | null;
  costoUnitarioMateriale: number;
  /** Il listino lordo prima dello sconto. Si scrive a mano: spesso sta su un'offerta. */
  prezzoListinoUnitario?: number | null;
  unitaMisura?: string | null;
  siglaProduttore?: string | null;
  codiceArticoloProduttore?: string | null;
  opzionale?: boolean;
  visibileInStampa?: boolean;
  /*  Omesso vuol dire «lascia com'è»: il modulo della riga non deve riaprire una
      distinta che qualcuno aveva chiuso solo perché non conosce questo campo.  */
  distinta?: string | null;
  prezzoBloccato?: boolean;
  ricaricoSpecificoPercentuale?: number | null;
  /*  Lo sconto al cliente su questa riga o su questo capitolo, da 0 a 100. Si eredita
      dal capitolo come il ricarico: vuoto torna a ereditare, zero lo spegne.  */
  scontoClientePercentuale?: number | null;
  giaComprensivoDiMargine?: boolean;
}

/** Un importo oppure una percentuale, mai tutti e due: il server rifiuta il resto. */
export interface SpesaDaScrivere {
  tipo: string;
  descrizione: string | null;
  importo: number | null;
  percentuale: number | null;
  /*  Se la voce si somma al totale oppure è un «di cui» da esporre. Assente vuol dire
      «decidi tu»: sulle nuove, la sicurezza nasce come «di cui» e le altre sommate; su
      una che esiste già, resta com'era.  */
  nelTotale?: boolean | null;
  /*  Se la voce si legge in offerta come riga del riepilogo, o sta dentro il prezzo
      senza dirsi. Assente vuol dire «lascia com'era»: accendere di nascosto una voce
      vuol dire scrivere una riga nuova sull'offerta che il cliente legge.  */
  visibileInStampa?: boolean | null;
}

/** Le sedi configurate. La sede decide il numero, e un numero non si cambia più. */
export interface SedeConfigurata {
  codice: string;
  denominazione: string;
  /** Quella senza prefisso nel numero: è la proposta predefinita. */
  principale: boolean;
}

export interface NuovoPreventivo {
  codiceClienteMago: string;
  descrizioneInterna: string;
  codiceCategoria: string;
  codiceSede?: string | null;
  oggettoPerCliente?: string | null;
}

/*  Cosa di questa offerta si stampa a parole: tutto insieme, non un campo per volta.
    Chi compila apre il pannello, scrive la nota, spunta le clausole e preme salva —
    e quel gesto deve riuscire tutto o niente, su un documento che va al cliente.  */
export interface TestiPreventivo {
  notaPerCliente: string | null;
  allaCorteseAttenzione: string | null;
  /*  Di che lavoro parla l'offerta: «Elettrico», «Meccanico», … Decide quali clausole
      predefinite si trovano gia' spuntate quando la si apre, e quali si vedono
      filtrando. Null vuol dire «non deciso».  */
  categoriaLavoro: string | null;
  letteraId: string | null;
  codicePagamentoMago: string | null;
  testiScelti: string[];
  /** Null le lascia come sono; una lista, anche vuota, le sostituisce tutte. */
  vociLibere?: VoceLiberaLetta[] | null;
}

/** Una voce scritta a mano su questa offerta, sotto un titolo delle condizioni. */
export interface VoceLiberaLetta {
  titoloId: string;
  testo: string;
}

/*  La copia di un'offerta: numero nuovo, cliente eventualmente altro, dentro tutto
    quello che c'era. Serve a farsi dei modelli — un impianto tipo scritto una volta e
    riusato — e non è una revisione: quella è un'altra versione dello stesso lavoro.

    Il cliente si richiede sempre, anche quando è lo stesso: è mezzo secondo speso per
    non intestare per sbaglio a un'azienda un'offerta che era di un'altra. Categoria e
    sede vuote restano quelle dell'originale.  */
export interface CopiaPreventivo {
  codiceClienteMago: string;
  descrizioneInterna: string;
  codiceCategoria?: string | null;
  codiceSede?: string | null;
}

/*  La conferma: il cliente ha detto di sì, e con quale ordine. Il numero è obbligatorio
    perché è il riferimento che finira' sulla fattura, e chiederlo dopo vuol dire cercarlo
    in una mail di tre mesi fa.  */
export interface QuotaSpesaInCommessa {
  spesaId: string;
  importo: number;
}

/*  Specchio di TeraWorks.Application.Preventivi.Carrello (15/10/2026).  */
export interface VoceCarrelloLetta {
  id: string;
  preventivoId: string;
  numeroPreventivo: string | null;
  rigaId: string;
  descrizione: string;
  livello: number;
  righeSotto: number;
  esiste: boolean;
}

export interface AggiuntaCarrello {
  preventivoId: string;
  righe: string[];
}

export interface IncollaDalCarrello {
  /** Il capitolo o sottocapitolo dove incollare. Null: in fondo, solo capitoli. */
  dentroRigaId: string | null;
  voci?: string[] | null;
  svuota?: boolean;
}

/*  Il carrello personale: di chi e' collegato, letto e scritto dal server.  */
export const carrello = {
  leggi: () => api.get<VoceCarrelloLetta[]>('/carrello'),
  aggiungi: (aggiunta: AggiuntaCarrello) => api.post<VoceCarrelloLetta[]>('/carrello', aggiunta),
  //  La rotta risponde col carrello rimasto, ma il client generico delle cancellazioni
  //  non legge il corpo: si rilegge, che costa una chiamata e resta un modo solo.
  togli: async (voceId: string) => {
    await api.elimina(`/carrello/${voceId}`);
    return api.get<VoceCarrelloLetta[]>('/carrello');
  },
  svuota: () => api.elimina('/carrello'),
};

/*  Specchio di TeraWorks.Application.Preventivi.MailOfferta.  */
export interface PropostaMailOfferta {
  a: string | null;
  oggetto: string;
  testo: string;
  rispondiA: string | null;
  postaCollegata: boolean;
  perchePostaNo: string | null;
  allegatoPdf: boolean;
}

export interface MailOfferta {
  a: string;
  oggetto: string;
  testo: string;
  mostraPrezzi: boolean;
  prezziSoloSuiTotali: boolean;
  mostraDettaglioArticoli: boolean;
  mostraRedattore: boolean;
  scavalcaPrezziDoppi: boolean;
}

export interface ConfermaPreventivo {
  /*  Facoltativi dal 15/10/2026: il lavoro accettato senza un ordine scritto si conferma
      lo stesso. Senza numero la data non si manda.  */
  numeroOrdineCliente: string | null;
  dataOrdine: string | null;
  /*  Le righe che vanno in commessa, opzionali comprese se il cliente le ha prese.
      Assente o vuoto vuol dire «tutta l'offerta», che è il caso di quasi ogni conferma:
      da lì passa la strada di sempre e i conti sono identici a prima.  */
  righeInCommessa?: string[] | null;
  /*  Quanto di ogni spesa accessoria pesa sul lavoro che si va a fare. Una spesa che
      non compare vale zero e non «tutta»: chi conferma ha guardato l'elenco.  */
  quoteSpese?: QuotaSpesaInCommessa[] | null;
}

/*  Cosa si sta scegliendo di portare in commessa, prima di aver confermato: i due campi
    della conferma senza il numero d'ordine. Le quote che non compaiono qui NON valgono
    zero — come invece vale alla conferma — ma se le propone il server in proporzione: qui
    si sta guardando, non decidendo.  */
export interface SceltaInCommessa {
  righeInCommessa?: string[] | null;
  quoteSpese?: QuotaSpesaInCommessa[] | null;
}

/*  Quanto varrebbe la commessa prendendo questo. Il conto lo fa il server e non questa
    schermata: i soldi di un'offerta si contano in un posto solo, e una seconda formula
    scritta qui divergerebbe alla prima riga di dominio che cambia — sul budget di un
    cantiere.  */
export interface AnteprimaCommessa {
  /** Il prezzo concordato con cui la commessa nascerebbe. */
  prezzo: number;
  costoDiretto: number;
  budgetMateriali: number;
  budgetOre: number;
  budgetTrasferte: number;
  budgetTotale: number;
  /** Quanto pesa ogni spesa: il valore scritto a mano dove c'è, altrimenti la proposta. */
  quote: QuotaSpesaInCommessa[];
}

export interface EsitoConferma {
  preventivo: DettaglioPreventivo;
  commessaId: string;
  numeroCommessa: string;
  /** Falso quando la commessa c'era già: confermare due volte non ne crea due. */
  commessaCreata: boolean;
}

export const preventivi = {
  leggi: (id: string) => api.get<DettaglioPreventivo>(`/preventivi/${id}`),

  /** Apre un preventivo e consuma il suo numero: è il primo salvataggio. */
  crea: (nuovo: NuovoPreventivo) => api.post<DettaglioPreventivo>('/preventivi', nuovo),

  impostaTestata: (id: string, testata: TestataPreventivo) =>
    api.put<DettaglioPreventivo>(`/preventivi/${id}/testata`, testata),

  impostaCantiere: (id: string, cantiere: CantierePreventivo) =>
    api.put<DettaglioPreventivo>(`/preventivi/${id}/cantiere`, cantiere),

  /*  L'indirizzo si manda perché i chilometri si chiedono mentre è ancora nel campo:
      senza, il server calcolerebbe su quello salvato — un numero che cambia, sembra
      giusto e riguarda un altro posto.  */
  calcolaKm: (id: string, indirizzoCantiere?: string | null) =>
    api.post<CalcoloTrasferta>(`/preventivi/${id}/cantiere/calcolo`, {
      indirizzoCantiere: indirizzoCantiere ?? null,
    }),

  aggiungiRiga: (id: string, riga: NuovaRigaPreventivo) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/righe`, riga),

  modificaRiga: (id: string, rigaId: string, riga: ModificaRigaPreventivo) =>
    api.put<DettaglioPreventivo>(`/preventivi/${id}/righe/${rigaId}`, riga),

  /** Copia la riga sotto sé stessa; se è un capitolo, con tutto il suo contenuto. */
  duplicaRiga: (id: string, rigaId: string) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/righe/${rigaId}/copia`, {}),

  /*  Sposta la riga con tutto quello che le sta sotto. Il verso sta nell'indirizzo e non
      in un corpo: non c'è niente da decidere oltre a «questa riga, in quella direzione»,
      e nel registro delle richieste «sposta/dentro» dice cosa è successo.  */
  spostaRiga: (id: string, rigaId: string, verso: VersoSpostamento) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/righe/${rigaId}/sposta/${verso}`, {}),

  /*  Sposta la riga al numero d'ordine scritto nella prima colonna. Il numero va in
      chiaro nell'indirizzo, come il verso: «ordine/30» dice gia' tutto.  */
  spostaRigaAlCodice: (id: string, rigaId: string, codice: number) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/righe/${rigaId}/ordine/${codice}`, {}),

  /*  Rimette i numeri d'ordine in decine. Serve perche' si consumano: inserisci tre
      volte fra 10 e 20 e ti ritrovi 11,25.  */
  rinumeraRighe: (id: string) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/rinumera`, {}),

  rimuoviRiga: (id: string, rigaId: string) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/righe/${rigaId}/rimozione`, {}),

  /*  Il ciclo di vita. POST su un sottopercorso che nomina la regola e non PATCH su un
      campo «stato»: non si aggiorna un valore, si applica una transizione con le sue
      condizioni — un invio senza data di validità viene rifiutato.  */
  invia: (id: string) => api.post<DettaglioPreventivo>(`/preventivi/${id}/invio`, {}),
  /*  L'offerta per mail (15/10/2026): la proposta da leggere, poi l'invio vero, che
      congela il documento, compone il PDF e mette la mail in coda in un gesto solo.  */
  incolla: (id: string, incolla: IncollaDalCarrello) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/incolla`, incolla),
  mailOfferta: (id: string) => api.get<PropostaMailOfferta>(`/preventivi/${id}/mail-offerta`),
  inviaPerMail: (id: string, mail: MailOfferta) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/invio-mail`, mail),

  /*  Un secondo addetto ha riletto l'offerta. Non e' una transizione di stato — il
      documento dopo e' lo stesso di prima, con una firma in piu' — ma passa da un POST
      su un sottopercorso come le transizioni, perche' anche qui quello che si chiede al
      server e' di applicare una regola: chi ha scritto non puo' firmare. La firma cade
      alla prima modifica.  */
  controlla: (id: string) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/controllo`, {}),

  /*  Cosa di quello che non sta nelle righe si dichiara in offerta. Non cambia un euro
      del Totale: cambia quanto se ne racconta.  */
  dichiarazioni: (id: string, trasferta: TrasfertaInOfferta, costiGenerali: boolean) =>
    api.put<DettaglioPreventivo>(
      `/preventivi/${id}/dichiarazioni?trasferta=${trasferta}&costiGenerali=${costiGenerali}`,
      {},
    ),

  /*  Quanto varrebbe la commessa prendendo queste righe. Non scrive niente: si chiama
      mentre chi conferma spunta e despunta.  */
  anteprimaCommessa: (id: string, scelta: SceltaInCommessa) =>
    api.post<AnteprimaCommessa>(`/preventivi/${id}/anteprima-commessa`, scelta),

  conferma: (id: string, conferma: ConfermaPreventivo) =>
    api.post<EsitoConferma>(`/preventivi/${id}/conferma`, conferma),

  nonAggiudicato: (id: string) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/non-aggiudicazione`, {}),

  apriRevisione: (id: string) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/revisione`, {}),

  /*  La copia: risponde con il DETTAGLIO DELLA COPIA, come la revisione, perché è
      quello su cui si continua a lavorare.  */
  duplica: (id: string, copia: CopiaPreventivo) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/copia`, copia),

  /*  Riapre un confermato: torna «inviato», da dove si apre una revisione. Permesso suo,
      che ha l'ufficio e non il tecnico.  */
  riapriConfermato: (id: string) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/riapertura`, {}),

  impostaTesti: (id: string, testi: TestiPreventivo) =>
    api.put<DettaglioPreventivo>(`/preventivi/${id}/testi`, testi),

  /** Toglie il blocco alle righe e rilegge i costi dai listini, dicendo cosa si è mosso. */
  sbloccaPrezzi: (id: string) =>
    api.post<EsitoSbloccoPrezzi>(`/preventivi/${id}/prezzi/sblocco`, {}),

  /** Chi altro offre l'articolo di questa riga, e a quanto. Non scrive niente. */
  confrontoRiga: (id: string, rigaId: string) =>
    api.get<ConfrontoRiga>(`/preventivi/${id}/righe/${rigaId}/confronto`),

  /*  Prende il netto di un'offerta come costo della riga. Risponde col preventivo intero
      perché cambiare un costo cambia il margine, i costi generali e il prezzo: rifare
      quella catena qui vorrebbe dire una seconda copia delle formule del dominio.  */
  adottaOfferta: (id: string, rigaId: string, offertaId: string) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/righe/${rigaId}/prezzo-da-offerta`, {
      offertaId,
    } satisfies PrezzoDaOfferta),

  aggiungiSpesa: (id: string, spesa: SpesaDaScrivere) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/spese`, spesa),

  modificaSpesa: (id: string, spesaId: string, spesa: SpesaDaScrivere) =>
    api.put<DettaglioPreventivo>(`/preventivi/${id}/spese/${spesaId}`, spesa),

  rimuoviSpesa: (id: string, spesaId: string) =>
    api.post<DettaglioPreventivo>(`/preventivi/${id}/spese/${spesaId}/rimozione`, {}),

  /** Le sedi da cui può nascere un preventivo: con una sola non c'è niente da chiedere. */
  sedi: () => api.get<SedeConfigurata[]>('/configurazione/sedi'),
};

/*  Riesportata: chi lavora sul preventivo la prende da qui insieme al resto, senza dover
    sapere che il contratto vive nella scheda dell'articolo.  */
export type { CalcoloTrasferta, OffertaConfrontata };
